/**
 * Presence reducer + helpers (Collab Phase 10). Pure/injectable (no DOM): the
 * peer map, deterministic per-user colors, viewport-gated cursor visibility, a
 * send-side throttle gate, and join/leave linger. collab-ui renders from this.
 */

import { fnv1a64Hex } from '../shared/collab-protocol.js';

/** Deterministic, stable-per-user color: hsl(fnv1a(sub) % 360, 70%, 45%). */
export function peerColor(sub) {
  const hue = parseInt(fnv1a64Hex(String(sub)).slice(0, 8), 16) % 360;
  return `hsl(${hue} 70% 45%)`;
}

/**
 * Peer presence store. `setRoster` reconciles the connId roster (join/leave)
 * from peers-changed while preserving live presence data for survivors;
 * `applyPresence` folds in a presence frame; `prune` drops peers unseen past the
 * linger (reconnect flap protection).
 */
export function createPresenceStore({ now = () => Date.now(), lingerMs = 5000 } = {}) {
  const peers = new Map();

  function setRoster(list) {
    const ids = new Set((list || []).map((p) => p.connId));
    for (const id of [...peers.keys()]) if (!ids.has(id)) peers.delete(id);
    for (const p of list || []) {
      if (!p?.connId) continue;
      const cur = peers.get(p.connId) || {};
      peers.set(p.connId, {
        ...cur,
        connId: p.connId,
        sub: p.sub,
        email: p.email ?? cur.email ?? null,
        color: peerColor(p.sub),
        lastSeen: now()
      });
    }
  }

  function applyPresence(frame) {
    if (!frame?.connId) return;
    const cur = peers.get(frame.connId) || { connId: frame.connId };
    peers.set(frame.connId, {
      ...cur,
      sub: frame.sub ?? cur.sub,
      email: frame.email ?? cur.email ?? null,
      color: peerColor(frame.sub ?? cur.sub),
      cursor: frame.cursor ?? null,
      selection: frame.selection ?? null,
      editingSectionKey: frame.editingSectionKey ?? null,
      pageId: frame.pageId ?? null,
      lastSeen: now()
    });
  }

  function prune() {
    const t = now();
    for (const [id, p] of peers) if (t - (p.lastSeen || 0) > lingerMs) peers.delete(id);
  }

  return {
    setRoster,
    applyPresence,
    removePeer: (connId) => peers.delete(connId),
    prune,
    list: () => [...peers.values()],
    get: (connId) => peers.get(connId),
    clear: () => peers.clear()
  };
}

/**
 * Cursor visibility: a peer's live cursor shows only when they're on the same
 * viewport as the local user (avatars + section badges stay regardless). The
 * tooltip elsewhere says "viewing mobile" for the mismatch case.
 */
export function cursorVisible(peer, localViewportName) {
  if (!peer?.cursor) return false;
  return peer.cursor.viewportName === localViewportName;
}

/** Send-side throttle gate (≥ minIntervalMs between presence sends). */
export function createPresenceThrottle(minIntervalMs = 80) {
  let lastAt = -Infinity;
  return {
    due(now) {
      return now - lastAt >= minIntervalMs;
    },
    mark(now) {
      lastAt = now;
    }
  };
}
